/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {memo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '../../../theme/theme';
import {getAssetDetailsContentStyles} from './assetDetailsContentStyles';
import {LabelValueRow} from './LabelValueRow';
import {SpreaderMeasurement} from './SpreaderGranularInfo';

export interface SpreaderInfoRow extends SpreaderMeasurement {
  label: string;
}

export interface SpreaderInfoCardProps {
  // the card's title, e.g. `Conditions` ...
  title?: string;
  // a value shown on the title's own row, e.g. Granular's material `SALT` ...
  titleValue?: string;
  rows?: SpreaderInfoRow[];
  // an emphasized row below a divider, e.g. `Tank Level` ...
  total?: SpreaderInfoRow;
}

const useStyles = makeStyles(() => {
  const {valueText} = getAssetDetailsContentStyles();
  return {
    container : {
      boxSizing       : 'border-box',
      display         : 'flex',
      width           : '100%',
      flexDirection   : 'column',
      gap             : '16px',
      padding         : '16px',
      backgroundColor : dlsColor('white'),
      border          : `0.5px solid ${dlsColor('grey7')}`,
      borderRadius    : '8px'
    },
    titleRow : {
      display        : 'flex',
      width          : '100%',
      alignItems     : 'baseline',
      justifyContent : 'space-between',
      gap            : '8px'
    },
    title : {
      fontFamily    : 'Roboto',
      fontWeight    : 700,
      fontSize      : '14px',
      lineHeight    : '16px',
      letterSpacing : '1px',
      textTransform : 'uppercase',
      color         : dlsColor('grey2')
    },
    titleValue : valueText,
    rows       : {
      display       : 'flex',
      width         : '100%',
      flexDirection : 'column',
      gap           : '8px'
    },
    divider : {
      width     : '100%',
      height    : 0,
      border    : 'none',
      borderTop : `1px solid ${dlsColor('grey7')}`,
      margin    : 0
    }
  };
});

/**
 * SpreaderInfoCard renders one bordered Spreader detail card - an optional
 * uppercase title (with an optional value on its own row, like Granular's
 * material name), its label/value rows and an optional emphasized total
 * below a divider - for the Spreader accordion's per-manufacturer layouts
 * (Force America, Dickey-john, Cirus). It renders nothing without a title,
 * rows or total.
 *
 * ```typescript
 *
 *   import {SpreaderInfoCard} from
 *     '@ion/dls/src/components/assetdetails/current/SpreaderInfoCard';
 *   ...
 *
 *   <SpreaderInfoCard title='Liquid'
 *     rows={[
 *       {label: 'Liquid Target Rate', value: '31.68', unit: 'gal/mi'},
 *       {label: 'Liquid Actual Rate', value: '27.6594', unit: 'gal/mi'}
 *     ]}
 *     total={{label: 'Tank Level', value: 'N/A'}} />
 *
 * ```
 *
 * @param   title      The card's title.
 * @param   titleValue A value shown on the title's row.
 * @param   rows       The card's label/value rows.
 * @param   total      An emphasized row shown below a divider.
 * @returns            The Spreader detail card, or nothing when empty.
 */
export const SpreaderInfoCard = ({
  title,
  titleValue,
  rows = [],
  total
}: SpreaderInfoCardProps) => {
  const classes = useStyles();
  let card: React.ReactElement = null;

  if (title || rows.length || total) {
    card = (
      <div className={classes.container}>
        {title && (
          <div className={classes.titleRow}>
            <span className={classes.title}>
              {title}
            </span>
            {titleValue && (
              <span className={classes.titleValue}>
                {titleValue}
              </span>
            )}
          </div>
        )}
        {rows.length > 0 && (
          <div className={classes.rows}>
            {rows.map(function(row) {
              return (
                <LabelValueRow key={row.label}
                  label={row.label}
                  value={row.value}
                  unit={row.unit} />
              );
            })}
          </div>
        )}
        {total && rows.length > 0 && (
          <hr className={classes.divider} />
        )}
        {total && (
          <LabelValueRow label={total.label}
            value={total.value}
            unit={total.unit}
            emphasized />
        )}
      </div>
    );
  }

  return card;
};

export default memo(SpreaderInfoCard);
